// Transition gating for the russia-requests drawer (#944).
//
// Two layers (russia has no party-side gate; see WHY below):
//   1. State machine — `RUSSIA_REQUEST_TRANSITIONS` decides which
//      transitions are legal from which `RequestStatus`.
//   2. RBAC — every transition declares one or more `PermissionCode`s;
//      an action button is hidden when the user's permissions bundle
//      does not satisfy them.
//
// WHY: russia transitions do NOT carry a party-side matrix on the
//   backend (see `RUSSIA_REQUEST_TRANSITIONS` aiInvariant —
//   "the wire/server doesn't apply a TRANSITION_SIDE matrix; permission
//   plus `RussiaRequestsVisibility` together suffice"). So the russia
//   client gate is intentionally one layer thinner than china's.

import {
  RUSSIA_REQUEST_TRANSITIONS,
  hasPermission,
  type PermissionCode,
  type RussiaRequestTransitionKey,
} from '@magimp/shared';
import type { IconName } from '../../ui/index.js';
import type { PermissionsBundle } from '../../stores/auth.js';
import type { RequestStatus } from '@magimp/shared';

export type RussiaTransitionLabelKey =
  | 'actions.send'
  | 'actions.return'
  | 'actions.process'
  | 'actions.reopen'
  | 'actions.close'
  | 'actions.success'
  | 'actions.refuse'
  | 'actions.postpone'
  | 'actions.archive'
  | 'actions.unarchive';

export interface RussiaTransitionAction {
  key: RussiaRequestTransitionKey;
  to: RequestStatus;
  labelKey: RussiaTransitionLabelKey;
  icon: IconName;
  kind: 'primary' | 'secondary' | 'danger';
  needsReason?: boolean;
  needsConfirm?: boolean;
}

const ACTION_META: Record<
  RussiaRequestTransitionKey,
  Omit<RussiaTransitionAction, 'key' | 'to'>
> = {
  send: { labelKey: 'actions.send', icon: 'send-horizontal', kind: 'primary' },
  return: {
    labelKey: 'actions.return',
    icon: 'rotate-ccw',
    kind: 'danger',
    needsReason: true,
  },
  process: { labelKey: 'actions.process', icon: 'check-check', kind: 'primary' },
  // WHY (#1544): logist_ru-side reopen of a PROCESSED row back to IN_WORK.
  //   Reason is required (mirror of return).
  reopen: {
    labelKey: 'actions.reopen',
    icon: 'rotate-ccw',
    kind: 'secondary',
    needsReason: true,
  },
  // INVARIANT (#944 AC): destructive transitions surface a confirm modal
  //   before firing. For russia those are `close` (PROCESSED → CLOSED,
  //   commercially-final) and `archive` (CLOSED → ARCHIVE, hides the row
  //   from the default list scope). #1544 adds `success` / `refuse`
  //   (manager-side terminal outcomes) which are likewise final.
  close: { labelKey: 'actions.close', icon: 'lock', kind: 'primary', needsConfirm: true },
  // WHY (#1544): manager-side terminal outcomes. `success` requires a
  //   stored winner bid (picked on the carriers tab); `refuse` requires a
  //   structured reason. Both confirm before firing. The full success /
  //   refuse modals (winner picker, reason select) land in #1548; this
  //   minimal bar surfaces the buttons so the lifecycle is operable.
  success: {
    labelKey: 'actions.success',
    icon: 'check-check',
    kind: 'primary',
    needsConfirm: true,
  },
  refuse: { labelKey: 'actions.refuse', icon: 'x', kind: 'danger', needsConfirm: true },
  // WHY (#1837): parking is a neutral, reversible action — secondary tone,
  //   no confirm/reason modal, so it routes through a direct no-body
  //   transition mutation in the drawer's `onAction`/`fire` path. `clock`
  //   reads as "parked / pending decision".
  postpone: { labelKey: 'actions.postpone', icon: 'clock', kind: 'secondary' },
  archive: { labelKey: 'actions.archive', icon: 'archive', kind: 'secondary', needsConfirm: true },
  unarchive: { labelKey: 'actions.unarchive', icon: 'archive-restore', kind: 'secondary' },
};

export function allowedRussiaActions(
  status: RequestStatus,
  permissions: PermissionsBundle,
): RussiaTransitionAction[] {
  const out: RussiaTransitionAction[] = [];
  for (const key of Object.keys(RUSSIA_REQUEST_TRANSITIONS) as RussiaRequestTransitionKey[]) {
    // WHY: `close` is suppressed from the UI (finalisation routes through
    //   success/refuse); mirrors the China UI gate at
    //   `apps/web/src/features/requests/role.ts:236`. The backend matrix +
    //   grant remain unchanged — only the surface button is hidden.
    if (key === 'close') continue;
    const t = RUSSIA_REQUEST_TRANSITIONS[key];
    const fromList = t.from as readonly RequestStatus[];
    if (!fromList.includes(status)) continue;
    const permOk = (t.permissions as readonly PermissionCode[]).every((p) =>
      hasPermission(permissions, p),
    );
    if (!permOk) continue;
    out.push({ key, to: t.to, ...ACTION_META[key] });
  }
  return out;
}

/**
 * #1064 UI defense-in-depth gate: should the current user see UI
 * affordances that navigate from a Russia request to its China parent
 * (the drawer header `russia-request-drawer-parent-link` chip and any
 * future RF surface that links to `/china-rates`)?
 *
 * Mirrors the backend's `canSeeChinaParentFromRussia` rule in
 * `apps/backend/src/modules/russia-requests/visibility.ts` (#1063):
 *
 *   - `'ALL'` sentinel (admin)                    → true
 *   - holds `russiaRequests.scope.all`            → false (logist_ru)
 *   - holds `russiaRequests.transition.send`,
 *     and NOT `russiaRequests.scope.all`           → true (manager)
 *   - else                                        → false
 *
 * `scope.all` is the load-bearing differentiator: the seeded `logist_ru`
 * role holds BOTH `scope.all` AND every transition code (per #838 F-001),
 * so `transition.send` alone is NOT a unique manager signal. With #1063
 * landed, logist_ru already receives `parentRequestId: null` on the wire,
 * but this UI gate is the defense-in-depth layer the issue mandates:
 * the link MUST stay hidden even if a future backend regression
 * re-exposes the column.
 *
 * The web and backend helpers are deliberately NOT shared via
 * `packages/shared` — the web `PermissionsBundle` is `'ALL' | readonly
 * PermissionCode[]` while the backend uses `ReadonlySet<...> | 'ALL'`,
 * so a single helper would force a type-shape compromise on both sides
 * for one boolean.
 */
export function canSeeChinaParentFromRussia(permissions: PermissionsBundle): boolean {
  if (permissions === 'ALL') return true;
  if (permissions.includes('russiaRequests.scope.all')) return false;
  return permissions.includes('russiaRequests.transition.send');
}

/**
 * #1544 UI authority gate for the manager-side winner-bid pick control
 * on the carriers tab.
 *
 * Mirrors the backend's winner-pick rule: only a manager/admin may pick
 * the winning bid; the backend rejects a logist winner-pick with 403.
 * The manager-vs-logist distinction is the same as
 * `canSeeChinaParentFromRussia` — `scope.all` is the load-bearing
 * logist_ru signal (the seeded logist holds every transition code too,
 * per #838 F-001), so it must be checked before `transition.send`:
 *
 *   - `'ALL'` sentinel (admin)                    → true
 *   - holds `russiaRequests.scope.all`            → false (logist_ru)
 *   - holds `russiaRequests.transition.send`,
 *     and NOT `russiaRequests.scope.all`           → true (manager)
 *   - else                                        → false
 */
export function canPickRussiaWinner(permissions: PermissionsBundle): boolean {
  if (permissions === 'ALL') return true;
  if (permissions.includes('russiaRequests.scope.all')) return false;
  return permissions.includes('russiaRequests.transition.send');
}

/**
 * #1548 UI authority gate for the editable (logist_ru) cost section.
 *
 * Mirrors the backend carriers write gate (`russiaCarriers.manage`): an
 * actor holding it sees the full add/edit-carriers + bids + winner-pick
 * surface; everyone else gets the read-only cost view.
 */
export function canManageRussiaCost(permissions: PermissionsBundle): boolean {
  return hasPermission(permissions, 'russiaCarriers.manage');
}

/**
 * #1548 PII gate for the cost section. An actor lacking
 * `russiaCarriers.read` (a Manager) MUST NEVER receive or render any
 * carrier identity — they see only the identity-free
 * `RussiaRequest.primaryCostSummary`. Mirrors the backend
 * `canSeeRussiaCarrierPii` wire audience.
 *
 * @aiSecurity load-bearing: `false` selects the read-only cost branch
 *   that renders zero carrier-identity DOM.
 */
export function canSeeRussiaCarrierPii(permissions: PermissionsBundle): boolean {
  return hasPermission(permissions, 'russiaCarriers.read');
}

/**
 * @aiPurpose client-side visibility gate for the manual-create surface at
 *   `/russia-rates/new` (#706 Slice D). Returns `true` when the actor
 *   holds `russiaRequests.manage`. The router also wraps the `new`
 *   child route in a `PermissionGate` so deep links from a read-only
 *   user land on a forbidden surface instead of an editor that 403s on
 *   submit.
 * @aiSecurity advisory only — the backend chokepoint
 *   (`apps/backend/src/modules/russia-requests/russia-requests.controller.ts`,
 *   `POST /russia-requests/manual`) re-applies the same permission. A
 *   flipped client predicate cannot grant access.
 */
export function canCreateRussiaRequestManual(permissions: PermissionsBundle): boolean {
  return hasPermission(permissions, 'russiaRequests.manage');
}

/**
 * @aiPurpose client-side visibility gate for the Duplicate action on the
 *   Russia drawer header (#2120). `true` for the request OWNER (the actor is
 *   the row `managerId` AND holds the author-identity capability
 *   `russiaRequests.transition.send`) OR admin — mirror of the backend
 *   owner-or-admin gate in `RussiaRequestsDuplicateService` (itself a mirror
 *   of `assertWinnerAuthority`). Status-blind (any source status may be
 *   duplicated); hidden for a non-owner `scope.all` logist_ru so they never
 *   see an action the backend would 403.
 * @aiSecurity advisory only — the backend chokepoint re-applies the gate. A
 *   flipped client predicate cannot grant access; it only hides the UI.
 */
export function canDuplicateRussiaRequest(
  permissions: PermissionsBundle,
  actorUserId: string | null,
  party: { managerId: string },
): boolean {
  if (permissions === 'ALL') return true;
  if (actorUserId === null) return false;
  return party.managerId === actorUserId && permissions.includes('russiaRequests.transition.send');
}

/**
 * #1067 UI defense-in-depth gate for the RF drawer chat tab.
 *
 * The backend chat-party rule
 * (`apps/backend/src/modules/russia-requests/russia-requests-access.service.ts:isChatParty`)
 * is intentionally NARROWER than the RF row-visibility predicate: a
 * non-assigned logist_ru can view the row but MUST NOT see chat
 * messages, send replies, mark the thread unread, or open the
 * composer. The UI gate mirrors that rule so a visible-but-non-party
 * actor never sees the tab.
 *
 *   - `'ALL'` sentinel (admin)                                       → true
 *   - holds `russiaRequests.transition.send` and managerId === actor → true
 *     (the authoring manager)
 *   - holds `russiaRequests.scope.all` and logistId === actor        → true
 *     (the assigned/acting logist)
 *   - else                                                           → false
 *
 * @aiSecurity load-bearing: the backend rejects non-party calls with
 *   404 regardless, but the UI guarantees no composer/mark-unread
 *   button is mounted for non-parties so they never see the chat surface.
 */
export function canActAsRussiaRequestChatParty(
  permissions: PermissionsBundle,
  actorUserId: string | null,
  party: { managerId: string; logistId: string | null },
): boolean {
  if (permissions === 'ALL') return true;
  if (actorUserId === null) return false;
  if (party.managerId === actorUserId && permissions.includes('russiaRequests.transition.send')) {
    return true;
  }
  if (
    party.logistId !== null &&
    party.logistId === actorUserId &&
    permissions.includes('russiaRequests.scope.all')
  ) {
    return true;
  }
  return false;
}
